import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { OrgManager } from '@uplift/api';
import { EMPTY_STRING, ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';

import { ManagementTableColumnRenderArgs } from '../types';

export type ManagementCellProps = ManagementTableColumnRenderArgs<keyof OrgManager> & {
  field: keyof OrgManager | 'user.name' | 'user.email' | 'user.lastLogin' | 'profile.title';
  isCareApp?: boolean;
};

export const ManagementCell: React.VFC<ManagementCellProps> = ({ row, field, isCareApp }) => {
  const { classes } = useManagementCellStyles();
  const me = useCurrentuser();
  const isCurrentManager = me?.id === row?.user?.id;
  const name = `${row?.user?.firstName} ${row?.user?.lastName}`;
  const managerProfileLink = ROUTING_CONFIG.higherEducationManagerProfileDetails.replace(':managerId', row.user.id);
  const lastLoginValue = useMemo(() => {
    if (typeof row?.user?.lastLogin === 'string') {
      return CalendarDateService.date(row.user.lastLogin).fromNow();
    }
    return '–';
  }, [ row?.user?.lastLogin ]);

  const nameCellContentAdminApp = (
    <Box width="100%">
      <Link to={managerProfileLink} className={classes.link}>
        <Typography variant="body2" className={classes.nameAdminApp} noWrap={true} title={name}>{name}</Typography>
      </Link>
      <Typography variant="body2" className={classes.email} noWrap={true} title={row.user.email}>{row.user.email}</Typography>
    </Box>
  );

  const nameCellContentCareApp = (
    <Box width="100%">
      <Typography variant="body2" className={classes.nameCareApp} noWrap={true} title={name}>
        {name} <span className={classes.subtitle}>{isCurrentManager ? '(You)' : ''}</span>
      </Typography>
    </Box>
  );

  switch (field) {
    case 'role': {
      return (
        <Typography variant="body2" color="textPrimary" className={classes.capitalize}>{row[field] || EMPTY_STRING}</Typography>
      );
    }
    case 'user.name':
      return (
        isCareApp ? nameCellContentCareApp : nameCellContentAdminApp
      );
    case 'user.email':
      return (
        <Box width="100%">
          <Typography variant="body2" className={classes.email} noWrap={true}>{row.user.email}</Typography>
        </Box>
      );
    case 'profile.title':
      return (
        <Typography variant="body2" color="textPrimary" noWrap={true}>{row?.profile?.title || ''}</Typography>
      );
    case 'user.lastLogin':
      return (
        <Typography variant="body2" color="textPrimary">{lastLoginValue}</Typography>
      );
  }

  return (
    <Typography variant="body2" color="textPrimary">{row[field] || EMPTY_STRING}</Typography>
  );
};

const useManagementCellStyles = makeStyles({ name: 'ManagementCell' })(theme => ({
  link: {
    color: '#A63287',
    '&:hover': {
      color: '#DA64B7'
    },
    '&:visited': {
      color: '#74005A'
    },
  },
  nameAdminApp: {
    color: '#A63287',
    '&:hover': {
      color: '#DA64B7'
    },
    '&:visited': {
      color: '#74005A'
    },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  nameCareApp: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  email: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtitle: {
    fontWeight: '400 !important' as any,
    fontSize: '14px',
    color: theme.palette.text.secondary,
    lineHeight: '21px',
    paragraph: '16px',
    letterSpacing: '0.5px',
  }
}));
